import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export const LOGO_URL = "/apt-logo.jpg";

export const num = (v) => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

export const rupee = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
export const rupeePdf = (n) => "Rs. " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
export const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

export const inRange = (d, from, to) => {
  if (!d) return !(from || to);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
};

export const lastNDates = (n) => {
  const out = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

export const lastNMonths = (n) => {
  const out = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

export const monthLabel = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(y, Number(m) - 1, 1).toLocaleString("en-IN", { month: "short", year: "2-digit" });
};

export const CHART_COLORS = ["#2563eb", "#16a34a", "#eab308", "#dc2626", "#7c3aed", "#0891b2", "#ea580c", "#db2777", "#65a30d", "#4f46e5", "#64748b", "#0ea5e9", "#f59e0b", "#10b981"];

// -------- CSV export
export function exportCsv(filename, columns, rows) {
  const header = columns.map((c) => `"${c.label}"`).join(",");
  const body = rows.map((r) =>
    columns.map((c) => {
      const v = c.csv ? c.csv(r) : c.value ? c.value(r) : r[c.key];
      return `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    }).join(",")
  );
  const csv = [header, ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// -------- Logo dataURL cache (for PDF)
let _logoData = null;
export async function getLogoDataUrl() {
  if (_logoData !== null) return _logoData;
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    _logoData = await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    _logoData = null;
  }
  return _logoData;
}

// -------- Chart capture (Recharts SVG -> PNG dataURL)
export async function svgToPng(svg, scale = 2) {
  const box = svg.getBoundingClientRect();
  const w = Math.ceil(box.width) || 800;
  const h = Math.ceil(box.height) || 300;
  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", w);
  clone.setAttribute("height", h);
  const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
  style.textContent = "text{font-family:Helvetica,Arial,sans-serif;} .recharts-cartesian-axis-tick-value{fill:#64748b;}";
  clone.insertBefore(style, clone.firstChild);
  const xml = new XMLSerializer().serializeToString(clone);
  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
  });
  const canvas = document.createElement("canvas");
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.scale(scale, scale);
  ctx.drawImage(img, 0, 0, w, h);
  return { dataUrl: canvas.toDataURL("image/png"), w, h };
}

// Captures every element marked with data-chart-capture inside root
export async function captureCharts(root) {
  if (!root) return [];
  const out = [];
  const cards = root.querySelectorAll("[data-chart-capture]");
  for (const card of cards) {
    const svg = card.querySelector("svg.recharts-surface") || card.querySelector("svg");
    if (!svg) continue;
    try {
      const png = await svgToPng(svg);
      const legend = [...card.querySelectorAll(".recharts-legend-item-text")].map((n) => n.textContent).filter(Boolean);
      out.push({ title: card.getAttribute("data-chart-title") || "", legend, ...png });
    } catch (e) { /* skip unrenderable chart */ }
  }
  return out;
}

// -------- PDF export (with logo header + faint watermark)
export async function exportPdf({ title, subtitle, columns, rows, filename, charts = [] }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const logo = await getLogoDataUrl();

  const drawChrome = () => {
    if (logo) {
      try { doc.addImage(logo, "JPEG", 40, 26, 120, 30); } catch (e) {}
      // faint centered watermark
      try {
        if (doc.GState && doc.setGState) {
          doc.saveGraphicsState();
          doc.setGState(new doc.GState({ opacity: 0.06 }));
          doc.addImage(logo, "JPEG", W / 2 - 170, H / 2 - 42, 340, 84);
          doc.restoreGraphicsState();
        }
      } catch (e) {}
    }
    doc.setFontSize(14); doc.setFont(undefined, "bold"); doc.setTextColor(0);
    doc.text(title, W - 40, 44, { align: "right" });
    if (subtitle) {
      doc.setFontSize(9); doc.setFont(undefined, "normal"); doc.setTextColor(120);
      doc.text(subtitle, W - 40, 58, { align: "right" });
      doc.setTextColor(0);
    }
  };

  autoTable(doc, {
    startY: 80,
    head: [columns.map((c) => c.label)],
    body: rows.map((r) => columns.map((c) => (c.pdf ? c.pdf(r) : c.value ? c.value(r) : r[c.key] ?? ""))),
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [37, 99, 235] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 40, right: 40, top: 76 },
    didDrawPage: drawChrome,
  });

  // charts on following pages
  if (charts.length) {
    let y = 0;
    charts.forEach((c, i) => {
      const maxW = W - 80;
      const imgW = maxW;
      const imgH = Math.min((c.h / c.w) * imgW, 300);
      if (i === 0 || y + imgH + 60 > H - 50) {
        doc.addPage();
        drawChrome();
        y = 90;
      }
      doc.setFontSize(11); doc.setFont(undefined, "bold"); doc.setTextColor(30);
      doc.text(c.title || `Chart ${i + 1}`, 40, y);
      y += 12;
      try { doc.addImage(c.dataUrl, "PNG", 40, y, imgW, imgH); } catch (e) {}
      y += imgH + 8;
      if (c.legend && c.legend.length) {
        doc.setFontSize(8); doc.setFont(undefined, "normal"); doc.setTextColor(110);
        const lines = doc.splitTextToSize("Series: " + c.legend.join("  ·  "), maxW);
        doc.text(lines, 40, y);
        y += lines.length * 10;
      }
      y += 22;
    });
  }

  doc.setFontSize(8); doc.setFont(undefined, "normal"); doc.setTextColor(150);
  doc.text(`Apt Enterprises · Generated ${new Date().toLocaleString("en-IN")}`, 40, H - 22);
  doc.save(`${filename}.pdf`);
}
